import * as React from 'react';
import { Container, Grid } from '@mui/material';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import { visuallyHidden } from '@mui/utils';
import { _axios as axios, cancelToken, customFetch } from '../../utils/auth';
import { url } from '../../utils';
import ButtonGroupComponent from '../../components/ButtonGroup/ButtonGroup';
import SelectList from '../../components/SelectList/SelectList';

interface Data {
  name: string;
  count: number;
}

function createData(name: string, count: number): Data {
  return {
    name,
    count,
  };
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }

  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }

    return a[1] - b[1];
  });

  return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'name',
    numeric: false,
    disablePadding: true,
    label: 'Wording',
  },
  {
    id: 'count',
    numeric: true,
    disablePadding: false,
    label: 'Count',
  },
];

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}>
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}>
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default function Dashboard() {
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('count');
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [language, setLanguage] = React.useState<'th' | 'en' | 'cn'>('th');
  const [selectedShowGroup, setSelectedShowGroup] = React.useState<any>({
    all: true,
    weeks: false,
    months: false,
    years: false,
  });
  const [actionList, setActionList] = React.useState([]);
  const [categoriesList, setCategoriesList] = React.useState([]);
  const [locationTypeList, setLocationTypeList] = React.useState([]);
  const [locationsList, setlocationsList] = React.useState([]);
  const [locationBranchList, setlocationBranchist] = React.useState([]);
  const [actionFilter, setActionFilter] = React.useState<string>('');
  const [categoryFilter, setCategoryFilter] = React.useState<string>('');
  const [locationTypeFilter, setLocationTypeFilter] = React.useState<string>('');
  const [locationsFilter, setLocationsFilter] = React.useState<string>('');
  const [locationBranchFilter, setLocationBranchFilter] = React.useState<string>('');
  const [provinceFilter, setProvinceFilter] = React.useState<boolean>(false);
  const [data, setData] = React.useState<any>([]);

  const objectToArray = (object: any) => {
    if (!object) return [];

    const keyValue = Object.keys(object[0]);

    return object.map((value: any) => value[`${keyValue}`]);
  };

  const timeSelected = () => {
    let select = 0;
    const keys = Object.keys(selectedShowGroup);
    Object.values(selectedShowGroup).forEach((data, index) => {
      if (data) {
        select = index;
      }
    });

    return keys[select];
  };

  React.useEffect(() => {
    (async () => {
      const time = timeSelected();
      const responseHistory = await axios.get(
        `${url}/v1/history?time=${time}${actionFilter && `&action=${actionFilter}`}${
          categoryFilter && `&category=${categoryFilter}`
        }${locationTypeFilter && `&location_type=${locationTypeFilter}`}${
          locationsFilter && `&locations=${locationsFilter}`
        }${locationBranchFilter && `&location_branch=${locationBranchFilter}`}${
          provinceFilter ? `&province=${provinceFilter}` : ''
        }${language && `&language=${language}`}`,
      );
      const resultsHistory = responseHistory.data.data.map((history: any) => createData(history.name, history.count));

      const responseActions = await axios.get(`${url}/v1/actions?language=${language}`);
      setActionList(responseActions.data.data);
      const responseCategories = await axios.get(`${url}/v1/category?language=${language}`);
      setCategoriesList(responseCategories.data.data);
      const responseLocationType = await axios.get(`${url}/v1/location_type?language=${language}`);
      setLocationTypeList(responseLocationType.data.data);

      setData(resultsHistory);
    })();
  }, [
    selectedShowGroup,
    actionFilter,
    categoryFilter,
    locationTypeFilter,
    locationsFilter,
    locationBranchFilter,
    language,
    provinceFilter,
  ]);

  React.useEffect(() => {
    setActionFilter('');
    setCategoryFilter('');
    setLocationTypeFilter('');
    setLocationsFilter('');
    setLocationBranchFilter('');
  }, [language]);

  React.useEffect(() => {
    if (locationTypeFilter === '9999') {
      setLocationsFilter('');
      setLocationBranchFilter('');
      setlocationsList([]);
      setlocationBranchist([]);
    } else {
      (async () => {
        const responseLocations = await axios.get(
          `${url}/v1/locations/type/${locationTypeFilter}?language=${language}`,
        );
        setlocationsList(responseLocations.data.data);
        setlocationBranchist([]);
      })();

      if (locationTypeFilter === '0') {
        setProvinceFilter(true);
      }
    }
  }, [locationTypeFilter, language]);

  React.useEffect(() => {
    (async () => {
      const responseLocations = await axios.get(
        `${url}/v1/location_branch/type/${locationTypeFilter}/locations/${locationsFilter}?language=${language}`,
      );
      setlocationBranchist(responseLocations.data.data);
    })();
  }, [locationsFilter, language]);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

  return (
    <Container
      fixed
      sx={{
        marginTop: '2rem',
      }}>
      <Grid container justifyContent={'space-between'}>
        <Grid item xs={12} md={5}>
          <SelectList
            label={'Language'}
            value={language}
            setValue={setLanguage}
            list={[
              {
                value: 'th',
                name: 'th',
              },
              {
                value: 'en',
                name: 'en',
              },
              {
                value: 'cn',
                name: 'cn',
              },
            ]}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            marginBottom: '2rem',
          }}>
          <ButtonGroupComponent state={selectedShowGroup} setState={setSelectedShowGroup} />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        sx={{
          marginBottom: '2rem',
        }}>
        <Grid item xs={12} md={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <SelectList label={'Action'} value={actionFilter} setValue={setActionFilter} list={actionList} />
            </Grid>
            <Grid item xs={12} md={2}>
              <SelectList
                label={'Category'}
                value={categoryFilter}
                setValue={setCategoryFilter}
                list={categoriesList}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <SelectList
                label={'Location Type'}
                value={locationTypeFilter}
                setValue={setLocationTypeFilter}
                list={locationTypeList}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <SelectList
                label={'Locations'}
                value={locationsFilter}
                setValue={setLocationsFilter}
                list={locationsList}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <SelectList
                label={'Location Branch'}
                value={locationBranchFilter}
                setValue={setLocationBranchFilter}
                list={locationBranchList}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Box sx={{ width: '100%' }}>
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer>
            <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size={'medium'}>
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={data.length}
              />
              <TableBody>
                {/* if you don't need to support IE11, you can replace the `stableSort` call with:
              rows.slice().sort(getComparator(order, orderBy)) */}
                {stableSort(data, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    // const isItemSelected = isSelected(row.name);
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        // onClick={(event) => handleClick(event, row.name)}
                        role="checkbox"
                        // aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={row.name}
                        // selected={isItemSelected}
                      >
                        <TableCell component="th" id={labelId} scope="row" padding="none">
                          {row.name}
                        </TableCell>
                        <TableCell align="right">{row.count}</TableCell>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: 53 * emptyRows,
                    }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={data.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
    </Container>
  );
}
